// Tarification Ha Transport
// BERLINE & VAN partagent les MÊMES tarifs :
//   Forfaits (aller simple, identiques au retour) :
//     - CDG ↔ Paris ............ 129€
//     - CDG ↔ Disneyland ....... 129€
//     - Orly ↔ Disneyland ...... 129€
//     - Orly ↔ Paris ........... 110€
//   Hors forfait : minimum course 65€ • 3,10€/km
//   - Autres (Tesla, Sprinter, Minibus, Autocar) : devis sur demande

// Matrice de forfaits commune Berline & Van (clé = "zoneA|zoneB" trié alphabétiquement)
const BERLINE_VAN_FORFAITS = {
  'cdg|paris': 129,
  'cdg|disneyland': 129,
  'disneyland|orly': 129,
  'orly|paris': 110
};

export const PRICING = {
  'standard': {
    label: 'Standard',
    minPrice: 39,
    minKm: 15,
    perKm: 2.31,
    forfaitZones: ['cdg', 'orly', 'disneyland'],
    forfaitTo: ['paris', 'cdg', 'orly', 'disneyland'],
    forfaitPrice: 85
  },
  'berline-vip': {
    label: 'Berline',
    minPrice: 69,
    minKm: 0,
    perKm: 3.10,
    forfaitMatrix: BERLINE_VAN_FORFAITS
  },
  'classe-v': {
    label: 'Van',
    minPrice: 69,
    minKm: 0,
    perKm: 3.10,
    forfaitMatrix: BERLINE_VAN_FORFAITS
  }
};

/**
 * Détermine la zone tarifaire d'une adresse à partir des infos retournées
 * par l'API Adresse (label, postcode, city, context, coordinates).
 */
export function getZone(addr) {
  if (!addr) return null;
  const label = (addr.label || '').toLowerCase();
  const city = (addr.city || '').toLowerCase();
  const postcode = (addr.postcode || '').toString();
  const ctx = (addr.context || '').toLowerCase();

  // CDG / Roissy
  if (
    postcode === '95700' ||
    label.includes('charles de gaulle') || label.includes('charles-de-gaulle') ||
    label.includes('roissy') || city.includes('roissy') ||
    label.includes('cdg')
  ) return 'cdg';

  // Orly
  if (
    postcode === '94390' || postcode === '94310' ||
    label.includes('aéroport d\'orly') || label.includes('aeroport d\'orly') ||
    label.includes('aéroport orly') ||
    city === 'orly' || city.includes('orly') ||
    label.includes('orly aéroport') || label.includes('orly airport')
  ) return 'orly';

  // Disneyland Paris (Chessy / Marne-la-Vallée 77700, 77777)
  if (
    postcode === '77700' || postcode === '77777' || postcode === '77600' ||
    label.includes('disneyland') || label.includes('disney') ||
    city.includes('chessy') || city.includes('marne-la-vallée') || city.includes('marne la vallee') ||
    label.includes('marne-la-vallée') || label.includes('marne la vallee')
  ) return 'disneyland';

  // Paris intra-muros (75001-75020)
  if (postcode.startsWith('75') && postcode.length === 5) return 'paris';
  if (city === 'paris') return 'paris';

  return null;
}

// Haversine distance in km
export function haversineKm(a, b) {
  if (!a || !b) return null;
  const [lon1, lat1] = a;
  const [lon2, lat2] = b;
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s));
  return R * c;
}

/**
 * Calcule le prix estimé pour un trajet.
 * Retourne { kind: 'flat' | 'distance' | 'quote', price?, km?, label?, reason? }
 */
export function estimatePrice({ vehicleId, fromAddr, toAddr }) {
  const tariff = PRICING[vehicleId];

  // Catégories non tarifées → devis sur demande
  if (!tariff) {
    return { kind: 'quote', reason: 'category' };
  }

  const fromZone = getZone(fromAddr);
  const toZone = getZone(toAddr);

  // Forfait via matrice spécifique (clé "zoneA|zoneB" triée alphabétiquement)
  if (tariff.forfaitMatrix && fromZone && toZone && fromZone !== toZone) {
    const key = [fromZone, toZone].sort().join('|');
    const price = tariff.forfaitMatrix[key];
    if (price !== undefined) {
      return {
        kind: 'flat',
        price,
        label: tariff.label,
        zones: { from: fromZone, to: toZone }
      };
    }
  }

  // Forfait si zone départ ou arrivée dans la liste, et l'autre dans forfaitTo
  if (tariff.forfaitZones && fromZone && toZone) {
    const inForfait =
      (tariff.forfaitZones.includes(fromZone) && tariff.forfaitTo.includes(toZone)) ||
      (tariff.forfaitZones.includes(toZone) && tariff.forfaitTo.includes(fromZone));
    if (inForfait && fromZone !== toZone) {
      return {
        kind: 'flat',
        price: tariff.forfaitPrice,
        label: tariff.label,
        zones: { from: fromZone, to: toZone }
      };
    }
  }

  // Calcul à la distance (Haversine, factor 1.3 pour approximer trajet routier)
  if (fromAddr?.coordinates && toAddr?.coordinates) {
    const distKm = haversineKm(fromAddr.coordinates, toAddr.coordinates);
    if (distKm !== null) {
      // Approximation route ~1.3x vol d'oiseau
      const routeKm = distKm * 1.3;
      const computed = routeKm * tariff.perKm;
      const price = Math.max(tariff.minPrice, computed);
      return {
        kind: 'distance',
        price: Math.round(price),
        km: Math.round(routeKm),
        label: tariff.label,
        minApplied: computed < tariff.minPrice
      };
    }
  }

  // Pas assez d'infos → devis
  return { kind: 'quote', reason: 'no-coords' };
}

export const ZONE_LABELS = {
  cdg: 'Aéroport CDG',
  orly: 'Aéroport Orly',
  disneyland: 'Disneyland Paris',
  paris: 'Paris intra-muros'
};
